import { z } from 'zod';
import type React from 'react';

// Apoio stateless do FinanceList (régua de 400 linhas): rótulos, formatação,
// schema e componentes de tabela/modal — extraídos sem mudança de comportamento.
export const TYPE_LABELS: Record<string, string> = {
  INCOME: 'Entrada',
  EXPENSE: 'Saída',
  CONTRIBUTION: 'Contribuição',
};

export const TYPE_VARIANTS: Record<string, string> = {
  INCOME: 'success',
  EXPENSE: 'danger',
  CONTRIBUTION: 'info',
};

export const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PAID: 'Pago',
  CANCELLED: 'Cancelado',
  OVERDUE: 'Vencido',
  REFUNDED: 'Reembolsado',
  ARCHIVED: 'Arquivado',
};

export const STATUS_VARIANTS: Record<string, string> = {
  PENDING: 'warning',
  PAID: 'success',
  CANCELLED: 'default',
  OVERDUE: 'danger',
  REFUNDED: 'info',
  ARCHIVED: 'default',
};

export const PAYMENT_LABELS: Record<string, string> = {
  CASH: 'Dinheiro',
  CREDIT_CARD: 'Crédito',
  DEBIT_CARD: 'Débito',
  PIX: 'Pix',
  TRANSFER: 'Transferência',
  BANK_SLIP: 'Boleto',
  OTHER: 'Outro',
};

export function fmtCurrency(val: unknown): string {
  const n = Number(val);
  if (Number.isNaN(n)) return 'R$ 0,00';
  return `R$ ${n.toFixed(2)}`.replace('.', ',');
}

export const createFormSchema = z.object({
  type: z.string(),
  description: z.string().min(1, 'Descrição é obrigatória'),
  amount: z.string().min(1, 'Valor é obrigatório'),
  categoryId: z.string().optional(),
  dueDate: z.string().optional(),
  personId: z.string().optional(),
  paymentMethod: z.string().optional(),
  receiptRef: z.string().optional(),
});

export type CreateForm = z.infer<typeof createFormSchema>;

export function SummaryCard({
  label,
  value,
  color,
  accent,
}: {
  label: string;
  value: string;
  color: string;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: accent ? '2px solid var(--warning)' : '1px solid var(--line)',
        padding: '1rem 1.25rem',
        flex: 1,
      }}
    >
      <span
        style={{
          fontSize: '0.7rem',
          color: 'var(--faint)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </span>
      <p style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0.2rem 0 0', color }}>{value}</p>
    </div>
  );
}

export function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: '0.625rem 0.75rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'var(--muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </th>
  );
}

export function Td(props: React.HTMLAttributes<HTMLTableCellElement>) {
  return (
    <td style={{ padding: '0.625rem 0.75rem', fontSize: '0.875rem', ...props.style }}>
      {props.children}
    </td>
  );
}

export function PagBtn({
  active,
  disabled,
  onClick,
  label,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        minWidth: 32,
        height: 32,
        padding: '0 0.5rem',
        border: active ? '1px solid var(--green)' : '1px solid var(--line)',
        borderRadius: 10,
        background: active ? 'var(--green)' : '#fff',
        color: active ? '#fff' : 'var(--muted)',
        fontSize: '0.8rem',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {label}
    </button>
  );
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
      <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--danger)', fontSize: '0.7rem' }}>{error}</span>}
    </label>
  );
}

export function Skeleton() {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: '1px solid var(--line)',
        padding: '1.25rem 1.5rem',
      }}
    >
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: '2rem',
            padding: '0.5rem 0',
            borderBottom: i < 4 ? '1px solid var(--line-soft)' : 'none',
          }}
        >
          <div style={{ width: 80, height: 14, background: 'var(--line-soft)', borderRadius: 8 }} />
          <div
            style={{ width: 200, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
          />
          <div style={{ width: 80, height: 14, background: 'var(--line-soft)', borderRadius: 8 }} />
          <div
            style={{ width: 100, height: 14, background: 'var(--line-soft)', borderRadius: 8 }}
          />
          <div style={{ width: 80, height: 14, background: 'var(--line-soft)', borderRadius: 8 }} />
          <div style={{ width: 80, height: 14, background: 'var(--line-soft)', borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}

export const inputCss: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  outline: 'none',
};

export const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

export const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--overlay)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

export const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  padding: '1.5rem',
  minWidth: 460,
  maxWidth: 520,
  maxHeight: '90vh',
  overflow: 'auto',
};
